<template>
  <div class="feedback-box content flags-section">
    <eg-b-overlay
      :show="flagsManuallyLoading"
      variant="white"
      :opacity="1"
      no-fade
    >
      <template #overlay>
        <div class="text-center">
          <eg-b-icon
            icon="arrow-clockwise"
            variant="info"
            font-scale="7"
            animation="spin"
          ></eg-b-icon>
          <h5 class="mt-2">{{ $t("essays.runDetectionReports") }}</h5>
        </div>
      </template>
      <flags-empty
        v-if="hasAiFlags"
        @clickFlagsManually="onClickFlagsManually"
      />

      <div v-else>
        <template v-if="isV3">
          <ai-detection-flag
            :flag-data="getAdditionalContentItem('usedAI')"
            :items="v3Flags.groupedItemsFlags['usedAI']"
            :config="types.usedAI"
            :is-no-ranges="isNoRanges"
            :active-highlight-index="activeHighlightIndex"
            @updateActiveHighlightIndex="
              $emit('updateActiveHighlightIndex', $event)
            "
            @updateHighlights="$emit('updateHighlights', $event)"
          />

          <!-- <grammar-flags-base-items
            :grouped-items="v3Flags.groupedItemsFlags"
            :types="types"
            :is-no-ranges="isNoRanges"
            :active-highlight-index="activeHighlightIndex"
            collapsable
            @updateActiveHighlightIndex="
              $emit('updateActiveHighlightIndex', $event)
            "
            @updateHighlights="$emit('updateHighlights', $event)"
          >
            <template #additionalContentBeforeList="{ type }">
              <base-item
                :item="getAdditionalContentItem(type)"
                :index="-2"
                next-step-title="Pro tip:"
              />
            </template>
          </grammar-flags-base-items> -->
        </template>

        <template v-else>
          <template v-for="(flag, index) in flags">
            <component
              :is="getComponent(flag)"
              :key="index"
              :flag="flag"
              class="flag-wrapper pb-4 mb-4"
            />
          </template>
        </template>
      </div>
    </eg-b-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import { createFlagsManually } from "@/api";
import { uniqBy } from "lodash";
import FlagsEmpty from "@/components/essay/essay/flags/v2/flags_empty.vue";
import FlagUsedAi from "@/components/essay/essay/flags/v2/flag_used_ai.vue";
import GrammarFlagsBaseItems from "@/components/essay/essay/v3/grammar_flags_base_items.vue";
import BaseItem from "@/components/essay/essay/v3/base_item.vue";
import factoryFlagUsedAiAnalysis from "@/components/essay/essay/flags/v3/flag_used_ai_analysis";
import factoryFlagUsedAiSuggestions from "@/components/essay/essay/flags/v3/flag_used_ai_suggestions";
import factoryFlagPlagiarismAnalysis from "@/components/essay/essay/flags/v3/flag_plagiarism_analysis";
import factoryFlagPlagiarismSuggestions from "@/components/essay/essay/flags/v3/flag_plagiarism_suggestions";
import factoryTemplateCheckingAnalysis from "@/components/essay/essay/flags/v3/flag_template_checking_analysis";
import factoryTemplateCheckingSuggestions from "@/components/essay/essay/flags/v3/flag_template_checking_suggestions";
import AiDetectionFlag from "@/components/essay/essay/flags/v4/ai_detection_flag.vue";
import i18n from "@/i18n/i18n";

const getTYPES = () => ({
  usedAI: {
    componentV2: FlagUsedAi,
    color: "#027BFF",
    background: "#E6F2FF",
    title: i18n.t("essays.aiDetectionResults"),
    analysis: (flag) => Vue.extend(factoryFlagUsedAiAnalysis(flag)),
    suggestions: (flag) => Vue.extend(factoryFlagUsedAiSuggestions(flag)),
  },
  plagiarism: {
    color: "#CE316C",
    background: "#FFEAEC",
    title: i18n.t("essays.plagiarismResults"),
    analysis: (flag) => Vue.extend(factoryFlagPlagiarismAnalysis(flag)),
    suggestions: (flag) => Vue.extend(factoryFlagPlagiarismSuggestions(flag)),
  },
  templateChecking: {
    componentV2: null,
    color: "#CF326C",
    background: "#E6F2FF",
    title: i18n.t("essays.relevance"),
    analysis: (flag) => Vue.extend(factoryTemplateCheckingAnalysis(flag)),
    suggestions: (flag) => Vue.extend(factoryTemplateCheckingSuggestions(flag)),
  },
});

export default {
  name: "EssayFlags",

  components: {
    FlagsEmpty,
    FlagUsedAi,
    GrammarFlagsBaseItems,
    BaseItem,
    AiDetectionFlag,
  },

  // NOTE: base_item.vue is using this, should be always false for flags tab
  provide: {
    specialForCopyPaste: false,
  },

  props: {
    essay: {
      type: Object,
      required: true,
    },

    activeHighlightIndex: {
      type: Number,
      default: -1,
    },
  },

  data() {
    return {
      types: getTYPES(),
      flagsManuallyLoading: false,
    };
  },

  computed: {
    flags() {
      return this.essay.flags.filter((flag) => flag.type === "usedAI");
    },

    hasAiFlags() {
      return !this.flags.filter((flag) => flag.type === "usedAI")?.length;
    },

    isNoRanges() {
      return (this.essay?.tags || []).includes("no-ranges");
    },

    proPlan() {
      return this.$store.getters.proPlan;
    },

    premiumPlan() {
      return this.$store.getters.premiumPlan;
    },

    isV3() {
      const isV3 = (flag) => (flag?.tags || []).includes("v3");
      return this.flags.some(isV3);
    },

    v3Flags() {
      const cleanDuplicates = this.flags.map((flag) => ({
        ...flag,
        ranges: uniqBy(flag.ranges || [], JSON.stringify),
      }));

      return cleanDuplicates.reduce(
        (acc, flag) => {
          acc.groupedItemsFlags[flag.type] = flag.ranges
            ? flag.ranges.map((range, index) => ({
                id: flag.type + index,
                type: flag.type,
                analysis: this.essay.content.slice(range[0], range[1]),
                ranges: [range],
              }))
            : [];

          return acc;
        },
        {
          groupedItemsFlags: {},
        }
      );
    },
  },

  methods: {
    getAdditionalContentItem(type) {
      const flag = this.essay.flags.find((f) => f.type === type);

      return {
        flag,
        analysis: this.types[type].analysis(flag),
        suggestions: this.types[type].suggestions(flag),
      };
    },

    getComponent(flag) {
      return this.types[flag.type].componentV2;
    },

    async onClickFlagsManually() {
      if (!(this.proPlan || this.premiumPlan)) {
        this.$bvModal.show("upgrade-modal");

        return;
      }

      this.flagsManuallyLoading = true;

      const {
        data: { flags },
      } = await createFlagsManually(this.essay.id, [{ type: "usedAI" }]);

      this.$emit("updateEssay", { ...this.essay, flags });
      this.flagsManuallyLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.feedback-box {
  padding: 24px;
}

.flags-section {
  white-space: normal;
  margin: 24px;
}

.flag-wrapper {
  //   // TODO: Temporarily hide the border as for normal users they only see 1 flag.
  /* border-bottom: 1px solid #dfdfdf; */

  &:v-deep {
    .flag-title {
      color: var(--Text-Body, #212529);
      font-size: 20px;
      font-weight: 500;
      line-height: 24px;
    }
  }
}
</style>
